import { useCallback } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import type { Id } from 'react-toastify'
import { toast } from 'react-toastify'

import {
  sendGTMEvent,
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  showSnackbar,
} from '@b-stock/fe-core/bstock-react'
import type { CreateWatchedListingDto } from '@b-stock/saved-search-api-client'

import { redirectToAuth } from '@/helpers/auth'
import { FeatureFlags } from '@/helpers/featureFlags'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { GTMEcommerceItem } from '@/helpers/types/gtmTypes'
import { TimeoutError, withTimeout } from '@/helpers/withTimeout'
import {
  fetchListingNote,
  usePutListingNote,
} from '@/queries/listingNotesQuery'
import {
  allWatchedListingsQuery,
  usePutWatchedListing,
  useDeleteWatchedListing,
  watchedStatusQuery,
} from '@/queries/watchlistQuery'

import { NOTE_READ_TIMEOUT_MS } from './noteReadTimeout'
import WatchlistErrorToastContent from './WatchlistErrorToastContent'
import WatchlistRemovalToastContent from './WatchlistRemovalToastContent'
import WatchlistToastContent from './WatchlistToastContent'

const assertSuccess = (status: number, statusText: string) => {
  if (status < 200 || status >= 300) {
    throw new Error(statusText)
  }
}

const showUndoErrorToast = () =>
  showSnackbar({
    content: <WatchlistErrorToastContent isRemoving={false} isUndoRestore />,
    options: {
      type: 'error',
      group: 'watchlist',
      autoClose: true,
      closeButton: true,
    },
  })

/** The pre-removal note read: `isKnown` is false when the read failed. */
type NoteRead = { isKnown: boolean; note: string | undefined }

/** Everything one toggle needs, supplied per call rather than per hook. */
type WatchlistToggleTarget = {
  listingId: string
  dateExpires: string
  /** The listing's status *before* this toggle. */
  isWatchlisted: boolean
  gtmItem?: GTMEcommerceItem
  currency?: string
  listingEndTime?: string
  storefrontName?: string
  // Called after a successful undo-restore, so the caller can reverse any
  // state it applied on removal (e.g. re-add the listing to a search
  // allowlist, or flip its heart back). Only meaningful on the delete path.
  onUndo?: () => void
}

type UseWatchlistToggler = {
  /**
   * Whether a buyer is signed in. Exposed so a caller can gate its own state
   * on auth before calling, rather than entering a pending state for a click
   * that only ever redirects.
   */
  isSignedIn: boolean
  /**
   * Resolves to `true` when the mutation succeeded and `false` when it was
   * caught and turned into an error toast — or when the buyer is signed out,
   * in which case they have been sent to auth.
   */
  toggleWatchlisting: (target: WatchlistToggleTarget) => Promise<boolean>
}

/**
 * The watchlist toggle — mutations, toasts, GTM and undo — with the listing
 * supplied per call instead of per hook.
 *
 * {@link useToggleWatchlisting} is the wrapper to reach for on a surface that
 * shows one listing: it adds the optimistic `isCurrentlyWatchlisted` and
 * `isPending` state a single control needs.
 *
 * Use this one directly only where a *set* of listings shares one toggle and
 * per-listing state is held elsewhere. **It exists for performance.** Calling
 * the wrapper once per listing is correct but not free: every instance brings
 * its own `useFlag` — a LaunchDarkly subscription apiece — plus two
 * `useMutation`s and the access-token, analytics and query-client context
 * reads. Across a List View's ~40 visible rows, re-running on every grid
 * render, that is the cost the epic's List View load-time guardrail is
 * measured against. Hoisted here it is one of each for the whole grid, however
 * many rows are on screen.
 */
export const useWatchlistToggler = (): UseWatchlistToggler => {
  const isClosingAlertEnabled = useFlag(FeatureFlags.WATCHLIST_CLOSING_ALERT)
  // On: a removal keeps the buyer's note (keepNote=true) and Undo never
  // touches it. Off: saved-search clears the note on removal, so Undo puts it
  // back (MRM-362).
  const keepNote = useFlag(FeatureFlags.NOTES_OUTLIVE_WATCHLIST)
  const accessToken = useAccessToken()
  const decodedAccessToken = useDecodedAccessToken()
  const { personId, accountId } = decodedAccessToken ?? {}
  const { trackButtonClicked } = useAnalytics()
  const queryClient = useQueryClient()

  const { mutateAsync: putWatchedListing } = usePutWatchedListing()
  const { mutateAsync: deleteWatchedListing } = useDeleteWatchedListing()
  const { mutateAsync: putListingNote } = usePutListingNote()

  const invalidateWatchlistQueries = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [watchedStatusQuery.name] })
    // Refresh the bulk watched-listings cache that enriches ALP/Storefront
    // cards, so status stays correct across pagination and refetch.
    void queryClient.invalidateQueries({
      queryKey: [allWatchedListingsQuery.name],
    })
  }, [queryClient])

  /**
   * Re-creates a just-deleted watched-listing record. There is no
   * soft-delete/restore endpoint on the saved-search API, so undo is a fresh
   * create rather than a real reversal. The PUT never carries `note`:
   * `noteToRestore` is set only when the removal cleared the note (flag off),
   * and is put back through the listing-notes API.
   *
   * `getToastId` rather than the id itself: the toast this dismisses is the one
   * carrying this very handler, so its id does not exist yet when it is built.
   */
  const undoRemoval = useCallback(
    async (
      target: WatchlistToggleTarget,
      noteToRestore: string | undefined,
      getToastId: () => Id | null
    ) => {
      const toastId = getToastId()
      if (toastId != null) toast.dismiss(toastId)

      if (!personId || !accountId) return

      const { dateExpires, listingEndTime, listingId, onUndo } = target

      try {
        const createWatchedListingDto: CreateWatchedListingDto = {
          accountId,
          dateExpires,
          ...(isClosingAlertEnabled && listingEndTime
            ? { listingEndTime }
            : {}),
        }
        const { status, statusText } = await putWatchedListing({
          personId,
          listingId,
          createWatchedListingDto,
        })

        assertSuccess(status, statusText)
      } catch (error) {
        console.error('Error undoing watchlist removal', error)
        showUndoErrorToast()
        return
      }

      trackButtonClicked({
        buttonName: 'undo',
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        listing_id_pairings: [
          `${target.storefrontName ?? 'unknown'}: ${listingId}`,
        ],
      })
      // The listing is watched again even if its note fails to come back.
      onUndo?.()

      try {
        if (noteToRestore) {
          const { status, statusText } = await putListingNote({
            personId,
            listingId,
            accountId,
            note: noteToRestore,
          })
          assertSuccess(status, statusText)
        }
      } catch (error) {
        console.error('Error restoring the note on watchlist undo', error)
        showUndoErrorToast()
      } finally {
        // After the note write, so the watched reads (which carry `note`)
        // refetch with it.
        invalidateWatchlistQueries()
      }
    },
    [
      accountId,
      invalidateWatchlistQueries,
      isClosingAlertEnabled,
      personId,
      putListingNote,
      putWatchedListing,
      trackButtonClicked,
    ]
  )

  /**
   * The buyer's note on the listing, through the listing-notes query (cached
   * when fresh, fetched otherwise), so every surface sees it whether or not it
   * reads notes itself. `isKnown` is false when the read fails or has not
   * settled within {@link NOTE_READ_TIMEOUT_MS}: a removal never waits longer
   * on the note to go ahead, it falls back to today's copy.
   */
  const readNote = useCallback(
    async (buyerPersonId: string, listingId: string): Promise<NoteRead> => {
      try {
        const note = await withTimeout(
          fetchListingNote(queryClient, accessToken, buyerPersonId, listingId),
          NOTE_READ_TIMEOUT_MS
        )
        return { isKnown: true, note }
      } catch (error) {
        // A slow read is an expected fallback, not an error.
        if (!(error instanceof TimeoutError)) {
          console.error(
            'Error reading the note before watchlist removal',
            error
          )
        }
        return { isKnown: false, note: undefined }
      }
    },
    [accessToken, queryClient]
  )

  /**
   * Deletes the watched listing and shows the Undo toast. Throws when the
   * delete fails. The note picks the toast copy when it is kept (flag on), and
   * is what Undo puts back when the delete cleared it (flag off).
   *
   * Flag off, a note that could not be read could not be put back by Undo, so
   * that one removal sends keepNote=true and the server keeps the note: a
   * removal never loses a note it cannot restore.
   */
  const removeListing = useCallback(
    async (buyerPersonId: string, target: WatchlistToggleTarget) => {
      const { listingId } = target
      const noteReadPromise = readNote(buyerPersonId, listingId)
      // Flag off, the DELETE clears the note: read it first so Undo can put it
      // back. Flag on, the note survives, so the read runs beside the DELETE.
      const noteReadBeforeDelete = keepNote ? undefined : await noteReadPromise
      const keepsNote = keepNote || noteReadBeforeDelete?.isKnown === false

      const { status, statusText } = await deleteWatchedListing({
        personId: buyerPersonId,
        listingId,
        keepNote: keepsNote,
      })

      assertSuccess(status, statusText)
      const { note } = await noteReadPromise

      const noteToRestore = keepsNote ? undefined : note
      // showSnackbar's 'action' toasts render no close button (see its own
      // doc comment), and clicking Undo doesn't dismiss a toast on its own
      // — hold the id so the undo handler can close it explicitly. Scoped
      // to this call rather than a hook-wide ref, because one toggler now
      // serves many listings.
      let removalToastId: Id | null = null
      removalToastId = showSnackbar({
        content: (
          <WatchlistRemovalToastContent
            hasKeptNote={keepNote && !!note}
            onUndo={() =>
              undoRemoval(target, noteToRestore, () => removalToastId)
            }
          />
        ),
        options: {
          type: 'default',
          group: 'watchlist',
          autoClose: 'action',
          closeButton: true,
        },
      })
    },
    [deleteWatchedListing, keepNote, readNote, undoRemoval]
  )

  const toggleWatchlisting = useCallback(
    async (target: WatchlistToggleTarget): Promise<boolean> => {
      if (!personId || !accountId) {
        redirectToAuth()
        return false
      }

      const {
        currency,
        dateExpires,
        gtmItem,
        isWatchlisted,
        listingEndTime,
        listingId,
      } = target

      try {
        if (isWatchlisted) {
          await removeListing(personId, target)
        } else {
          // create
          const createWatchedListingDto: CreateWatchedListingDto = {
            accountId,
            dateExpires,
            ...(isClosingAlertEnabled && listingEndTime
              ? { listingEndTime }
              : {}),
          }
          const { status, statusText } = await putWatchedListing({
            personId,
            listingId,
            createWatchedListingDto,
          })

          assertSuccess(status, statusText)

          const wishlistItem = gtmItem || {
            item_id: listingId,
            item_name: '',
            price: 0,
            quantity: 1,
            index: 1,
            currency: currency ?? 'USD',
            listingId,
          }
          sendGTMEvent({ ecommerce: null })
          sendGTMEvent({
            event: 'add_to_wishlist',
            platform: GTM_PLATFORM,
            ecommerce: {
              currency: wishlistItem.currency,
              value: wishlistItem.price || undefined,
              items: [wishlistItem],
            },
          })

          trackButtonClicked({
            buttonName: 'add_to_watchlist',
            listing_id: listingId,
          })

          const handleViewWatchlistClick = () => {
            trackButtonClicked({
              buttonName: 'view_watchlist',
            })
          }

          showSnackbar({
            content: (
              <WatchlistToastContent
                onViewWatchlistClick={handleViewWatchlistClick}
              />
            ),
            options: {
              type: 'success',
              group: 'watchlist',
              autoClose: 'action',
              closeButton: true,
            },
          })
        }

        invalidateWatchlistQueries()
        return true
      } catch (error) {
        console.error('Error adding listing to watchlist', error)

        showSnackbar({
          content: <WatchlistErrorToastContent isRemoving={isWatchlisted} />,
          options: {
            type: 'error',
            group: 'watchlist',
            autoClose: true,
            closeButton: true,
          },
        })
        return false
      }
    },
    [
      accountId,
      invalidateWatchlistQueries,
      isClosingAlertEnabled,
      personId,
      putWatchedListing,
      removeListing,
      trackButtonClicked,
    ]
  )

  return { isSignedIn: !!personId && !!accountId, toggleWatchlisting }
}
